import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Switch,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import {
  buildMultiplePrescriptionsPrintHtml,
  prescriptionHasMultipleVias,
  prescriptionPrintQueueLabel,
  type PrescriptionPrintPayload,
} from '../../utils/prescriptionPrintDocument';
import { printClinicHtml } from '../../utils/clinicPrintHtml';
import {
  CLOUD_API_THEME,
  cloudApiDialogHeaderSx,
  cloudApiDialogPaperSx,
  cloudApiPrimaryButtonSx,
} from '../../pages/whatsappCloudApiTheme';

export type PrescriptionPrintQueueItem = {
  id: string;
  label: string;
  printData: PrescriptionPrintPayload;
};

type Props = {
  open: boolean;
  onClose: () => void;
  items: PrescriptionPrintQueueItem[];
  /** Valor inicial do switch «somente 1ª via». */
  initialFirstViaOnly?: boolean;
};

const PrescriptionBatchPrintDialog: React.FC<Props> = ({
  open,
  onClose,
  items,
  initialFirstViaOnly = false,
}) => {
  const [queue, setQueue] = useState<PrescriptionPrintQueueItem[]>([]);
  const [firstViaOnly, setFirstViaOnly] = useState(initialFirstViaOnly);

  useEffect(() => {
    if (!open) return;
    setQueue(items);
    setFirstViaOnly(initialFirstViaOnly);
  }, [open, items, initialFirstViaOnly]);

  const hasMultipleVias = useMemo(
    () => queue.some((item) => prescriptionHasMultipleVias(item.printData?.prescription)),
    [queue],
  );

  const previewHtml = useMemo(
    () => buildMultiplePrescriptionsPrintHtml(
      queue.map((q) => q.printData),
      { firstViaOnly },
    ),
    [queue, firstViaOnly],
  );

  const removeItem = (id: string) => {
    setQueue((prev) => prev.filter((q) => q.id !== id));
  };

  const handlePrint = () => {
    if (!queue.length) return;
    printClinicHtml(previewHtml, {
      title: queue.length > 1 ? `Receituários (${queue.length})` : 'Receituário',
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: cloudApiDialogPaperSx }}>
      <DialogTitle sx={{ ...cloudApiDialogHeaderSx, justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography component="span" sx={{ fontWeight: 700 }}>
            Impressão de receituários
          </Typography>
          {queue.length > 0 && (
            <Typography component="span" sx={{ ml: 1, fontSize: '0.85rem', color: CLOUD_API_THEME.slateFaint }}>
              ({queue.length} {queue.length === 1 ? 'receita' : 'receitas'})
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {hasMultipleVias && (
            <FormControlLabel
              control={<Switch size="small" checked={firstViaOnly} onChange={(e) => setFirstViaOnly(e.target.checked)} />}
              label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Somente 1ª via</Typography>}
              sx={{ mr: 1 }}
            />
          )}
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            disabled={!queue.length}
            sx={cloudApiPrimaryButtonSx}
          >
            Imprimir
          </Button>
          <IconButton onClick={onClose} aria-label="Fechar">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0, bgcolor: CLOUD_API_THEME.muted }}>
        {queue.length > 1 && (
          <Box sx={{ px: 2, py: 1.25, bgcolor: '#fff', borderBottom: '1px solid #e2e8f0', display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            <Typography variant="caption" sx={{ color: '#64748b', alignSelf: 'center', mr: 0.5 }}>
              Na impressão:
            </Typography>
            {queue.map((item) => (
              <Chip
                key={item.id}
                size="small"
                label={item.label}
                onDelete={() => removeItem(item.id)}
                sx={{ maxWidth: '100%' }}
              />
            ))}
          </Box>
        )}
        {queue.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">Nenhuma receita selecionada para impressão.</Typography>
          </Box>
        ) : (
          <iframe
            title="Pré-visualização dos receituários"
            srcDoc={previewHtml}
            style={{ width: '100%', height: '70vh', border: 'none', background: '#fff' }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PrescriptionBatchPrintDialog;

/** Monta fila a partir dos IDs recém-criados (GET `/print` por receita). */
export async function buildPrescriptionPrintQueueFromIds(
  fetchPrintData: (prescriptionId: string) => Promise<PrescriptionPrintPayload | null>,
  savedList: Array<{ id?: string; prescription_number?: string }>,
): Promise<PrescriptionPrintQueueItem[]> {
  const queue: PrescriptionPrintQueueItem[] = [];
  for (const saved of savedList) {
    const id = saved?.id;
    if (!id) continue;
    const printData = await fetchPrintData(id);
    if (!printData) continue;
    const label = saved.prescription_number
      ? `${saved.prescription_number}${printData.prescription?.patient_name ? ` — ${printData.prescription.patient_name}` : ''}`
      : prescriptionPrintQueueLabel(printData);
    queue.push({ id, label, printData });
  }
  return queue;
}
